import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X } from 'lucide-react'
import gardienImg from '../../assets/images/gardien.jpg'
import iconeGardienImg from '../../assets/images/IconeGardien.png'

interface CreditsModalProps {
  isOpen: boolean
  onClose: () => void
}

const CreditsModal: React.FC<CreditsModalProps> = ({ isOpen, onClose }) => {
  // Fermer avec Escape
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 flex items-center justify-center"
          style={{ zIndex: 99999 }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <motion.div
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={onClose}
          />

          {/* Modal Plein Écran */}
          <motion.div
            className="relative w-full h-full flex"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
          >
            {/* Bouton Fermer */}
            <button
              onClick={onClose}
              className="absolute top-6 right-6 z-50 p-3 rounded-full transition-all duration-200"
              style={{
                backgroundColor: 'rgba(212, 175, 55, 0.2)',
                backdropFilter: 'blur(10px)'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.3)'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'rgba(212, 175, 55, 0.2)'}
            >
              <X className="w-6 h-6" style={{ color: '#d4af37' }} />
            </button>

            {/* COLONNE GAUCHE - L'Image */}
            <div className="w-2/5 relative overflow-hidden">
              <img
                src={gardienImg}
                alt="Le Gardien de la Mémoire"
                className="w-full h-full object-cover"
                style={{ objectPosition: 'center' }}
                onError={(e) => {
                  console.log('Image not found, using placeholder')
                  e.currentTarget.style.background = 'linear-gradient(135deg, #1c1917 0%, #44403c 100%)'
                  e.currentTarget.style.display = 'flex'
                  e.currentTarget.style.alignItems = 'center'
                  e.currentTarget.style.justifyContent = 'center'
                  // Note: Inner HTML injection is risky/ugly for react, but keeping as is for minimal diff, just fixing src
                  // Actually, let's fix the src inside the innerHTML string too, although imports won't work there easily.
                  // BETTER: Render a fallback element instead involving JSX if onError triggers.
                  // For now, I'll assume the import works and onError is less likely.
                  // But if I must fix the innerHTML string, I'd need the path.
                  // Importing gives the final path.
                  e.currentTarget.innerHTML = `<img src="${iconeGardienImg}" alt="Gardien" style="width: 120px; height: 120px; filter: drop-shadow(0 4px 8px rgba(212, 175, 55, 0.5));" />`
                }}
              />

              {/* Dégradé noir en bas */}
              <div
                className="absolute bottom-0 left-0 right-0 h-32"
                style={{
                  background: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)'
                }}
              />
            </div>

            {/* COLONNE DROITE - Le Texte */}
            <div
              className="w-3/5 flex flex-col justify-center px-16 py-12"
              style={{
                backgroundColor: '#1c1917',
                backgroundImage: `
                  radial-gradient(circle at 1px 1px, rgba(255,255,255,0.05) 1px, transparent 0),
                  radial-gradient(circle at 2px 2px, rgba(255,255,255,0.03) 1px, transparent 0)
                `,
                backgroundSize: '20px 20px, 40px 40px'
              }}
            >
              {/* Titre Principal */}
              <motion.h1
                className="text-4xl font-serif mb-6"
                style={{
                  color: '#d4af37',
                  fontFamily: "'Playfair Display', serif",
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                L'Architecte de la Mémoire
              </motion.h1>

              {/* Corps du texte */}
              <motion.div
                className="space-y-4 mb-8"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <p
                  className="text-lg leading-relaxed"
                  style={{
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontFamily: "'Inter', sans-serif",
                    lineHeight: '1.8'
                  }}
                >
                  <strong style={{ color: '#d4af37' }}>Réminiscence</strong> est né d'une volonté de transformer nos souvenirs
                  en terrain de jeu. Conçu et imaginé pour reconnecter les générations entre elles,
                  ce jeu invite à redécouvrir l'art de la narration collective.
                </p>

                <p
                  className="text-base leading-relaxed"
                  style={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontStyle: 'italic'
                  }}
                >
                  Chaque partie devient une œuvre unique, tissée par l'imagination des joueurs
                  et guidée par la sagesse du Gardien. Un hommage à la mémoire humaine
                  et à sa capacité infinie de créer des histoires.
                </p>
              </motion.div>

              {/* Signature */}
              <motion.div
                className="space-y-4 border-t pt-8"
                style={{ borderColor: 'rgba(212, 175, 55, 0.3)' }}
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <div className="space-y-3">
                  <div>
                    <p style={{ color: '#d4af37', fontWeight: '600', marginBottom: '4px' }}>
                      <strong>Concept & Game Design :</strong>
                    </p>
                    <p style={{ color: 'rgba(255, 255, 255, 0.9)', fontSize: '0.95rem' }}>
                      Grégory Le Terte
                    </p>
                    <a
                      href="https://www.linkedin.com/in/gregoryleterte/"
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#d4af37',
                        fontSize: '0.85rem',
                        textDecoration: 'none',
                        opacity: 0.8,
                        transition: 'opacity 0.2s'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
                    >
                      🔗 LinkedIn Profile
                    </a>
                  </div>

                  <p style={{ color: '#d4af37', fontWeight: '600' }}>
                    <strong>Direction Artistique :</strong> <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Assistée par IA</span>
                  </p>
                  <p style={{ color: '#d4af37', fontWeight: '600' }}>
                    <strong>Développement :</strong> <span style={{ color: 'rgba(255, 255, 255, 0.9)' }}>Assisté par IA</span>
                  </p>
                </div>

                {/* Copyright */}
                <motion.p
                  className="text-sm pt-4"
                  style={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    borderTop: '1px solid rgba(212, 175, 55, 0.2)',
                    paddingTop: '1rem'
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                >
                  © 2025 - Tous droits réservés
                </motion.p>
              </motion.div>

              {/* Ornement décoratif */}
              <motion.div
                className="absolute bottom-8 right-16"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 1 }}
              >
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2) 0%, rgba(212, 175, 55, 0.1) 100%)',
                    border: '2px solid rgba(212, 175, 55, 0.3)'
                  }}
                >
                  <img
                    src={iconeGardienImg}
                    alt="Gardien"
                    className="w-8 h-8"
                    style={{ filter: 'drop-shadow(0 2px 4px rgba(212, 175, 55, 0.3))' }}
                  />
                </div>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default CreditsModal
